// @flow
import { Fragment, useEffect, useMemo, useState } from 'react';
import { View } from 'react-native';
import config from '../../../config';
import formatUnitType from '../../../lib/TripicaUnitTypeFormatter';
import { OptionSlider } from '../../common/CharacteristicSlider';
import { hasNoUnlimitedOptionChoice } from '../../common/CharacteristicSlider/lib/hasUnlimitedOptionChoice';
import { DataHelpModal } from './components';

const PlanSliders = (props: Props) => {
  const [isDataHelpModalOpen, setIsDataHelpModalOpen] = useState(false);

  // 1. Mémorisation des options filtrées par ORDER_CATEGORY_RESTRICTED
  // On ne recalcule que si les options brutes ou le type d'ordre changent
  const filteredCreditableOptions = useMemo(() => {
    if (!props.creditableOptions) return [];
    return props.creditableOptions.map(creditableOption => {
      return creditableOption.filter(option => {
        let isOK = true;
        option?.characteristics?.forEach(charac => {
          if (charac.name === 'ORDER_CATEGORY_RESTRICTED') {
            if (!charac?.value.includes(props.orderType)) {
              isOK = false;
            }
          }
        });
        return isOK;
      })

    });
  }, [props.creditableOptions, props.orderType]);

  // 2. Mémorisation des options sélectionnées
  const filteredSelectedOptions = useMemo(() => {
    if (!props.selectedOptions) return [];

    return props.selectedOptions.map(selectedOption => {
      const allOptions = filteredCreditableOptions.flat();
      const foundOption = allOptions.find((option: Object) => option.ouid === selectedOption.ouid);

      if (!foundOption) {
        return allOptions.find((option: Object) => option.type === selectedOption.type);
      }
      return selectedOption;
    }).filter(Boolean);
  }, [props.selectedOptions, filteredCreditableOptions]);

  // 3. Logique métier : Ajout des options par défaut au montage
  //    Skip if Redux already has a selection for this slider group
  //    (avoids resetting user choices when the component remounts across stepper steps)
  useEffect(() => {
    const defaultConfigs = [
      { type: 'DATA', configKey: 'defaultDataOptionName' },
      { type: 'VOICE', configKey: 'defaultVoiceOptionName' },
    ];

    filteredCreditableOptions.forEach(options => {
      if (options.length === 0) return;

      const optionType = options[0]?.type;
      const alreadySelected = props.selectedOptions?.some(
        sel => options.some(opt => opt.ouid != null && opt.ouid === sel.ouid),
      );

      // Chercher une config par défaut pour ce type
      const defaultConfig = defaultConfigs.find(cfg => cfg.type === optionType);
      const defaultName = defaultConfig && config[defaultConfig.configKey];
      const shouldForceDefault =
        props.isInitialMount &&
        config.displayRoamingPlanSliders &&
        defaultName;

      if (shouldForceDefault) {
        const preferred = options.find(o => o.name === defaultName);
        if (preferred) {
          props.addOptionToNewOffer(preferred);
          return;
        }
      }

      if (!alreadySelected) {
        props.addOptionToNewOffer(options[0]);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDataHelpModal = () => setIsDataHelpModalOpen(true);
  const closeDataHelpModal = () => setIsDataHelpModalOpen(false);

  const smallBubble = useMemo(() =>
    hasNoUnlimitedOptionChoice(props.creditableOptions),
    [props.creditableOptions]);

  return (
    <View style={props.isUniqueOffer ? { height: 0, width: 0 } : null}>
      {filteredCreditableOptions.map((options, sliderIndex) => {
        if (options.length === 0) return null;

        const unitType = options[0].type;
        let displayOptions = options;

        if (props.isChangingPlanDuringTurnBackOn) {
          displayOptions = options.filter(
            option => option.name !== 'KAIZEN_OPTDATAUNL',
          );
        }

        if (unitType === 'SMS') {
          return null; // Ne pas afficher le slider SMS
        }

        return (
          <Fragment key={unitType}>
            <OptionSlider
              testID={`changePlan-item-${sliderIndex}`}
              sliderTestID={`changePlan-slider-${sliderIndex}`}
              smallBubble={smallBubble}
              options={displayOptions}
              displayIncludedForFirstOptionValue={config.displayIncludedForFirstOptionValue}
              onSelect={props.addOptionToNewOffer}
              name={unitType}
              selectedOptions={filteredSelectedOptions}
              onPressHelpIcon={
                formatUnitType(unitType) === 'DATA' ? openDataHelpModal : null
              }
              priceKey={props.priceKey}
              withZoneValidity={props.withZoneValidity}
              withSeparator
              orderType={props.orderType}
            />
          </Fragment>
        );
      })}
      <DataHelpModal
        visible={isDataHelpModalOpen}
        onRequestClose={closeDataHelpModal}
      />
    </View>
  );
};

export default PlanSliders;